import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { useApolloClient } from '@apollo/client';

import { QUERY } from '../gql-operations';
import { AuthContext } from '../util/context';
import { buildThreadVisibilityFilterInput } from '../util/threadVisibilityFilters';

function useUpdateGetFolderContent() {
    const client = useApolloClient();
    const [getContent] = useLazyQuery(QUERY.GET_FOLDER_CONTENT, {
        fetchPolicy: 'cache-first' // Changed from network-only to prevent cache resets
    });

    const {
        state: { showDeletedMessages, folderThreadVisibilityFilter }
    } = React.useContext(AuthContext);

    const getFolderContentVariableCandidates = folder => {
        const baseData = {
            folderId: folder.id,
            isTrash: showDeletedMessages,
            ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
        };

        return [
            { data: baseData },
            { data: { ...baseData, respectManualOrder: false } },
            { data: { ...baseData, respectManualOrder: true } }
        ];
    };

    const isMatchingOptimisticFolder = (existingFolder, incomingFolder) =>
        existingFolder?.id?.startsWith?.('optimistic-folder-') &&
        existingFolder.parent === incomingFolder.parent &&
        existingFolder.title === incomingFolder.title;

    const prependFoldersAndRelinkHead = (existingFolders, incomingFolders) => {
        const incomingIds = new Set(incomingFolders.map(folder => folder?.id).filter(Boolean));
        const filteredExistingFolders = existingFolders.filter(existingFolder => {
            if (incomingIds.has(existingFolder?.id)) {
                return false;
            }

            return !incomingFolders.some(incomingFolder =>
                isMatchingOptimisticFolder(existingFolder, incomingFolder)
            );
        });
        const nextFolders = [...incomingFolders, ...filteredExistingFolders];
        const firstExistingIndex = incomingFolders.length;

        if (incomingFolders.length > 0 && nextFolders[firstExistingIndex]) {
            nextFolders[firstExistingIndex] = {
                ...nextFolders[firstExistingIndex],
                before: incomingFolders[incomingFolders.length - 1].id
            };
        }

        return nextFolders;
    };

    const normalizeFolderForContent = folder => ({
        __typename: folder?.__typename || 'Folder',
        id: folder?.id,
        title: folder?.title,
        parent: folder?.parent || null,
        owner: folder?.owner || null,
        before: folder?.before || null,
        isPinned: Boolean(folder?.isPinned),
        pinnedRank: folder?.pinnedRank ?? null,
        deletedAt: folder?.deletedAt || null,
        lastViewedAt: folder?.lastViewedAt || null,
        lastMovedAt: folder?.lastMovedAt || null,
        manualMessageSortEnabled: Boolean(folder?.manualMessageSortEnabled),
        sharingStatus: {
            __typename: folder?.sharingStatus?.__typename || 'FolderSharingStatus',
            isShared: Boolean(folder?.sharingStatus?.isShared),
            shareCount: Number(folder?.sharingStatus?.shareCount || 0),
            isPublished: Boolean(folder?.sharingStatus?.isPublished)
        }
    });

    const updateCachedFolderContent = (folder, buildNextFolders) => {
        getFolderContentVariableCandidates(folder).forEach(variables => {
            try {
                const data = client.readQuery({
                    query: QUERY.GET_FOLDER_CONTENT,
                    variables,
                    returnPartialData: true // Allow partial data to avoid errors during pagination
                });

                if (!data || !data.getFolderContent) {
                    return;
                }

                // Preserve existing messages and cursor to prevent cache resets
                const existingMessages = data.getFolderContent.messages?.messages || [];
                const existingCursor = data.getFolderContent.messages?.cursor || null;
                const existingTargetMessageId = data.getFolderContent.messages?.targetMessageId || null;
                const existingMessagesTypename = data.getFolderContent.messages?.__typename || 'MessagesPaginationOutput';

                client.writeQuery({
                    query: QUERY.GET_FOLDER_CONTENT,
                    variables,
                    data: {
                        getFolderContent: {
                            ...data.getFolderContent,
                            folder: data.getFolderContent.folder,
                            parent: data.getFolderContent.parent,
                            path: data.getFolderContent.path,
                            folders: buildNextFolders(data.getFolderContent.folders || []),
                            messages: {
                                messages: existingMessages,
                                cursor: existingCursor,
                                targetMessageId: existingTargetMessageId,
                                __typename: existingMessagesTypename
                            },
                            __typename: data.getFolderContent.__typename
                        }
                    }
                });
            } catch (error) {
                // Folder was not opened yet, or this cache-key variant is not present.
            }
        });
    };

    const updateNewFolders = (folder, newFolders) => {
        const normalizedNewFolders = newFolders.map(normalizeFolderForContent);

        updateCachedFolderContent(folder, existingFolders => {
            return prependFoldersAndRelinkHead(existingFolders, normalizedNewFolders);
        });
    };

    const refreshFolder = folder => {
        getContent({
            variables: getFolderContentVariableCandidates(folder)[0]
        });
    };

    // folder - selectedFolder
    // folderToSplice - folder to splice from the selectedFolder
    const refreshFolderLocal = (folder, folderToSplice) => {
        updateCachedFolderContent(folder, existingFolders => {
            const updatedFolders = [...existingFolders];
            let folderIndex = updatedFolders.findIndex(folder => folder.id === folderToSplice.id);
            if (folderIndex >= 0) {
                updatedFolders.splice(folderIndex, 1);
            }

            return updatedFolders;
        });
    };

    return { updateNewFolders, refreshFolder, refreshFolderLocal };
}

export default useUpdateGetFolderContent;

// const updateApolloCache = (folder, newFolders) => {
//     const data = client.readQuery({
//         query: QUERY.GET_FOLDER_CONTENT,
//         variables: {
//             data: {
//                 folderId: folder.id
//             }
//         }
//     });

//     console.log('this is data from current folder', data);
//     console.log('incoming folders', newFolders);
//     // const foldersToUpdate = [];
//     // if (newFolders.length > 0) {
//     //     foldersToUpdate = [...data.getFolderContent.folders, ...newFolders];
//     // }

//     const newData = {
//         getFolderContent: {
//             folder: data.getFolderContent.folder,
//             folders: [...newFolders],
//             messages: {
//                 messages: data.getFolderContent.messages.messages,
//                 cursor: data.getFolderContent.messages.cursor,
//                 __typename: data.getFolderContent.messages.__typename
//             },
//             __typename: data.getFolderContent.__typename
//         }
//     };

//     client.writeQuery({
//         query: QUERY.GET_FOLDER_CONTENT,
//         variables: {
//             data: {
//                 folderId: folder.id
//             }
//         },
//         data: newData
//     });
// };
